import Vue from "vue";
import _ from "lodash";

/**
 * TableController 用于统一管理表格数据的加载、分页、筛选、选中等逻辑。
 * 用法示例：
 * ```js
 * import { TableController } from '@/controllers/TableController'
 *
 * export default {
 *   data() {
 *     return {
 *       tableCtrl: null
 *     }
 *   },
 *   created() {
 *     this.tableCtrl = new TableController({
 *       fetchData: this.fetchData,
 *       pageSize: 20,
 *       isClientPaged: false,
 *       keyField: 'id',
 *       queryParameters: { status: '', keyword: '' }
 *     })
 *   },
 *   methods: {
 *     async fetchData(params) {
 *       // 返回 Promise，格式 { list: [], totalCount: 100 }
 *       const res = await apiFetchTable(params)
 *       return { list: res.data, totalCount: res.total }
 *     }
 *   }
 * }
 * ```
 */

export class TableController {
  // Helper to get nested value by dot notation
  static getNestedValue(obj, path) {
    return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
  }
  constructor(options) {
    const me = this;
    // options: { fetchData, pageSize, isClientPaged, keyField, autoLoad, queryParameters, listField, totalField }
    me.loadMethod = options.loadMethod;
    me.pageSize = options.pageSize || 15;
    me.isClientPaged = !!options.isClientPaged;
    me.autoLoad = options.autoLoad !== false;
    me.keyField = options.keyField || "id";
    me.pageBase = typeof options.pageBase === "number" ? options.pageBase : 1; // 新增，默认1
    me.pageField = options.pageField || "page";
    me.pageSizeField = options.pageSizeField || "pageSize";
    me.listField = options.listField || "list"; // 新增，默认 list
    me.totalField = options.totalField || "totalCount"; // 新增，默认 totalCount
    me.queryParameters = Vue.observable(options.queryParameters || {});
    me.tableRef = options.tableRef || null; // 可选，绑定的表格组件引用
    me.state = Vue.observable({
      allDataList: [],
      tableData: [],
      loading: false,
      pagination: {
        page: 1,
        pageSize: me.pageSize,
        total: 0,
      },
      selectedItemsMap: [],
    });
    me.customFilter = options.customFilter || null; // 新增，外部自定义过滤函数
    if (me.autoLoad) {
      me.loadTableData();
    }
  }

  get selectedItems() {
    return this.state.selectedItemsMap;
  }

  get selectedCount() {
    return this.state.selectedItemsMap.length;
  }

  async refresh() {
    const me = this;
    await me.loadTableData();
  }

  async loadTableData() {
    const me = this;
    me.state.loading = true;
    try {
      // 计算实际请求的页码
      const realPage = me.isClientPaged
        ? me.pageBase
        : me.state.pagination.page - 1 + me.pageBase;
      const params = {
        [me.pageField]: realPage,
        [me.pageSizeField]: me.isClientPaged
          ? Number.MAX_SAFE_INTEGER
          : me.state.pagination.pageSize,
        ...me.queryParameters,
      };
      // 清理 undefined 或 NaN 的参数
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || Number.isNaN(params[key])) {
          delete params[key];
        }
      });
      // 调用外部提供的 fetchData 方法获取数据
      const response = await me.loadMethod(params);
      if (me.isClientPaged) {
        // 兼容 response 直接为数组、{list:[]}、{items:[]} 三种情况
        let list = [];
        if (Array.isArray(response)) {
          list = response;
        } else if (Array.isArray(response[me.listField])) {
          list = response[me.listField];
        } else if (Array.isArray(response.items)) {
          list = response.items;
        }
        me.state.allDataList = list;
        me.state.filteredData = list;
        me.state.pagination.total = list.length;
        me.updateCurrentPageData();
      }
      else {
        // 非客户端分页也兼容 items 字段
        let tableData = response[me.listField] || [];
        me.state.tableData = tableData;
        me.state.pagination.total = response[me.totalField] || tableData.length || 0;
        me.state.allDataList = tableData;
      }
      if (me.isClientPaged) {
        me.filterData(); // 仅客户端分页时做前端过滤
      }
      me.restoreSelectionToTableRef();
    } catch (e) {
      // 可加事件或回调
      // me.onError && me.onError(e)
    } finally {
      if (_.isNil(me.tableRef)) {
        me.state.loading = false;
      } else {
        //列表渲染后再设置为false，避免跨页选择算法坏掉。
        me.tableRef.$nextTick(() => {
          me.state.loading = false;
        });
      }
    }
  }

  setCurrentPage(page) {
    const me = this;
    if (typeof page === "number" && page >= me.pageBase) {
      me.state.pagination.page = page;
      if (me.isClientPaged) {
        me.updateCurrentPageData();
      } else {
        me.loadTableData();
      }
    }
  }

  setPageSize(size) {
    const me = this;
    if (typeof size === "number" && size > 0) {
      me.state.pagination.pageSize = size;
      me.state.pagination.page = me.pageBase; // 重置到第一页
      if (me.isClientPaged) {
        me.updateCurrentPageData();
      } else {
        me.loadTableData();
      }
    }
  }

  filterData() {
    const me = this;
    // If a custom filter is provided, use it
    if (me.customFilter) {
      me.state.filteredData = me.customFilter(me.state.allDataList);
    } else {
      // Default filtering logic
      const filters = { ...me.queryParameters };
      me.state.filteredData = me.state.allDataList.filter((item) => {
        return Object.keys(filters).every((key) => {
          const value = filters[key];
          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (value instanceof Array && value.length === 0)
          )
            return true;
          // Support nested field queries, e.g., config.type
          let fieldValue = item;
          if (key.includes(".")) {
            key.split(".").forEach((k) => {
              fieldValue = fieldValue ? fieldValue[k] : undefined;
            });
          } else {
            fieldValue = item[key];
          }
          // Support fuzzy and exact matching
          if (
            typeof value === "string" &&
            value.startsWith("%") &&
            value.endsWith("%")
          ) {
            // Fuzzy match, case insensitive
            const filterStr = value.replace(/%/g, "").toLowerCase();
            return String(fieldValue ?? "")
              .toLowerCase()
              .includes(filterStr);
          } else {
            // Exact match
            return fieldValue === value;
          }
        });
      });
    }
    me.state.pagination.total = me.state.filteredData.length;
    me.state.pagination.page = 1;
    me.updateCurrentPageData();
  }

  // setQueryParameters 只更新参数，然后统一调用 filterData
  setQueryParameters(params) {
    const me = this;
    Object.keys(params).forEach((key) => {
      me.queryParameters[key] = params[key];
    });
    if (me.isClientPaged) {
      me.filterData();
    } else {
      me.loadTableData(); // 服务端分页直接请求，不做前端filterData
    }
  }

  updateCurrentPageData() {
    const me = this;
    // 计算偏移量时要减去 pageBase
    if (!Array.isArray(me.state.filteredData) || me.state.filteredData.length === 0) {
      // 如果 filteredData 为空，不覆盖 tableData，避免被意外清空
      return;
    }
    const pageIndex = me.state.pagination.page - me.pageBase;
    const start = pageIndex * me.state.pagination.pageSize;
    const end = start + me.state.pagination.pageSize;
    me.state.tableData = me.state.filteredData.slice(start, end);
  }

  handleSelectionChange(selected) {
    const me = this;
    if (me.state.loading) return; // 如果正在加载数据，则不处理选中状态
    // 创建一个 Set，包含当前页数据的唯一标识符（支持嵌套字段）
    const currentPageIds = me.state.tableData.map((item) => TableController.getNestedValue(item, me.keyField));

    // 遍历当前页数据，更新选中状态
    for (let i = 0; i < me.state.tableData.length; i++) {
      const item = me.state.tableData[i];
      const itemId = TableController.getNestedValue(item, me.keyField);
      if (
        selected.some((selectedItem) => TableController.getNestedValue(selectedItem, me.keyField) === itemId)
      ) {
        // 如果当前页的某项在选中列表中，则添加到 selectedItemsMap
        if (!me.state.selectedItemsMap.some((i) => TableController.getNestedValue(i, me.keyField) === itemId)) {
          me.state.selectedItemsMap.push(item);
        }
      } else if (currentPageIds.includes(itemId)) {
        // 如果当前页的某项不在选中列表中，则从 selectedItemsMap 中移除
        me.state.selectedItemsMap = me.state.selectedItemsMap.filter(
          (i) => TableController.getNestedValue(i, me.keyField) !== itemId
        );
      }
    }
  }

  restoreSelectionToTableRef() {
    if (!this.tableRef) return;
    const me = this;
    this.tableRef.$nextTick(() => {
      for (let i = 0; i < me.state.tableData.length; i++) {
        const row = me.state.tableData[i];
        const isSelected = me.state.selectedItemsMap.some(
          (item) => TableController.getNestedValue(item, me.keyField) === TableController.getNestedValue(row, me.keyField)
        );
        me.tableRef.toggleRowSelection(row, isSelected);
      }
    });
  }

  clearQueryParameters() {
    const me = this;
    Object.keys(me.queryParameters).forEach((key) => {
      me.queryParameters[key] = undefined;
    });
  }

  deleteItemById(id) {
    const me = this;
    if (me.isClientPaged) {
      me.state.allDataList = me.state.allDataList.filter(
        (item) => TableController.getNestedValue(item, me.keyField) !== id
      );
      me.state.filteredData = me.state.filteredData.filter(
        (item) => TableController.getNestedValue(item, me.keyField) !== id
      );
      me.updateCurrentPageData();
    } else {
      me.state.tableData = me.state.tableData.filter(
        (item) => TableController.getNestedValue(item, me.keyField) !== id
      );
    }
    me.state.selectedItemsMap = me.state.selectedItemsMap.filter(
      (item) => TableController.getNestedValue(item, me.keyField) !== id
    );
  }

  deleteItemByIndex(index) {
    const me = this;
    if (me.isClientPaged) {
      const item = me.state.allDataList[index];
      if (item) {
        me.deleteItemById(item[me.keyField]);
      }
    } else {
      const item = me.state.tableData[index];
      if (item) {
        me.deleteItemById(item[me.keyField]);
      }
    }
  }

  setTableData(list) {
    const me = this;
    me.state.tableData = list;
    if (me.isClientPaged) {
      me.state.allDataList = list;
      me.state.filteredData = list;
      me.state.pagination.total = list.length;
      me.updateCurrentPageData();
    } else {
      me.state.allDataList = list;
      me.state.pagination.total = list.length;
    }
  }

  addItem(item) {
    const me = this;
    if (me.isClientPaged) {
      if (
        !me.state.allDataList.some((i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField))
      ) {
        me.state.allDataList.push(item);
        me.state.filteredData.push(item);
        me.updateCurrentPageData();
      } else {
        Vue.set(
          me.state.allDataList,
          me.state.allDataList.findIndex(
            (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
          ),
          item
        );
        Vue.set(
          me.state.filteredData,
          me.state.filteredData.findIndex(
            (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
          ),
          item
        );
        me.updateCurrentPageData();
      }
    } else {
      if (
        !me.state.tableData.some((i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField))
      ) {
        me.state.tableData.push(item);
      } else {
        Vue.set(
          me.state.tableData,
          me.state.tableData.findIndex(
            (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
          ),
          item
        );
      }
    }
  }

  updateItem(item) {
    const me = this;
    if (me.isClientPaged) {
      const index = me.state.allDataList.findIndex(
        (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
      );
      if (index !== -1) {
        Vue.set(me.state.allDataList, index, item);
        // 同步 filteredData
        const fIndex = me.state.filteredData.findIndex(
          (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
        );
        if (fIndex !== -1) {
          Vue.set(me.state.filteredData, fIndex, item);
        }
        me.updateCurrentPageData();
      } else {
        me.state.allDataList.push(item);
        me.state.filteredData.push(item);
        me.updateCurrentPageData();
      }
    } else {
      const index = me.state.tableData.findIndex(
        (i) => TableController.getNestedValue(i, me.keyField) === TableController.getNestedValue(item, me.keyField)
      );
      if (index !== -1) {
        Vue.set(me.state.tableData, index, item);
      } else {
        me.state.tableData.push(item);
      }
    }
  }
}
